import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Upload, AlertCircle, CheckCircle, X, FileText } from 'lucide-react';
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import './CargaMasivaGenerico.css';

/**
 * COMPONENTE GENÉRICO DE CARGA MASIVA
 * 
 * Este componente permite realizar cargas masivas de datos desde archivos Excel
 * para diferentes módulos (Operadores, Remolques, Tractos, etc.)
 * 
 * CARACTERÍSTICAS:
 * - Descarga plantilla Excel con validaciones embebidas (selectores desplegables)
 * - Validación de datos en tiempo real
 * - Procesamiento por lotes para evitar bloquear la UI
 * - Manejo de errores detallado por registro
 * - Cache de peticiones API para optimizar rendimiento
 * - Progreso visual durante carga y validación
 * 
 * USO:
 * 
 * import CargaMasivaGenerico from "../../components/CargaMasivaGenerico/CargaMasivaGenerico";
 * import { operadoresConfig } from "../../components/CargaMasivaGenerico/configuraciones";
 * 
 * <CargaMasivaGenerico config={operadoresConfig(idTransportista)} />
 * 
 * CONFIGURACIÓN (config prop):
 * {
 *   title: string,                    // Título del componente
 *   subtitle: string,                 // Subtítulo descriptivo
 *   templateName: string,             // Nombre del archivo Excel a descargar
 *   headers: string[],                // Columnas del Excel
 *   requiredFields: string[],         // Campos obligatorios
 *   apiLoaders: Array<{               // APIs a cargar para validaciones
 *     url: string,
 *     cacheKey: string
 *   }>,
 *   validations: Array<{              // Validaciones para selectores en Excel
 *     name: string,
 *     column: string,
 *     type: 'static' | 'api',
 *     cacheKey?: string,
 *     options?: string[],
 *     mapper?: (data) => string[]
 *   }>,
 *   customValidations: Array<{        // Validaciones personalizadas
 *     validator: (row, index, allData, apiCache) => string | null
 *   }>,
 *   buildDTO: (row, apiCache) => object,  // Construir DTO para API
 *   apiEndpoint: string,              // Endpoint para guardar datos
 *   apiMethod: string,                // Método HTTP (post, put)
 *   postProcess?: (response, idTransportista) => Promise<void>  // Post-procesamiento opcional
 * }
 */
const CargaMasivaGenerico = ({ config }) => {
  // ==================== ESTADO DEL COMPONENTE ====================
  
  // Datos del archivo cargado
  const [csvData, setCsvData] = useState([]);
  
  // Resultados de validación por cada fila
  const [validationResults, setValidationResults] = useState([]);
  
  // Estados de carga
  const [isLoading, setIsLoading] = useState(false);           // Procesando archivo
  const [isValidating, setIsValidating] = useState(false);     // Validando datos
  const [isLoadingData, setIsLoadingData] = useState(false);   // Subiendo a API
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false); // Generando plantilla
  const [templateProgress, setTemplateProgress] = useState(0); // Progreso de plantilla
  
  // Nombre del archivo seleccionado
  const [fileName, setFileName] = useState('');
  
  // Control de modales
  const [isModalOpen, setIsModalOpen] = useState(false);           // Modal de detalles
  const [showLoadingModal, setShowLoadingModal] = useState(false); // Modal de progreso
  
  // Cache de datos de APIs
  const [apiCache, setApiCache] = useState({});
  
  // Ref para mantener el cache sincronizado (evita stale closures)
  const apiCacheRef = useRef({});
  
  // Progreso de operaciones
  const [validationProgress, setValidationProgress] = useState({ current: 0, total: 0 });
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  
  // Resultados de carga a API
  const [loadingResults, setLoadingResults] = useState([]);

  // ==================== REFERENCIAS ====================
  
  const fileInputRef = useRef(null);
  
  // Cache de peticiones HTTP (evita duplicados)
  const requestCache = useRef(new Map());
  
  // Peticiones pendientes (evita múltiples llamadas simultáneas)
  const pendingRequests = useRef(new Map());

  // Invalidar cache cada vez que el componente se monta (nueva apertura)
  useEffect(() => {
    requestCache.current.clear();
    pendingRequests.current.clear();
  }, []);

  // ==================== DATOS DE SESIÓN ====================
  
  const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
  const usuario = JSON.parse(sessionStorage.getItem("usuario"));

  // ==================== FUNCIONES DE API ====================
  
  /**
   * Ejecuta una función con reintentos exponenciales en caso de error
   * @param {Function} fetchFn - Función a ejecutar
   * @param {number} maxRetries - Número máximo de reintentos
   * @param {number} baseDelay - Delay base en ms
   */
  /**
   * Ejecuta una función con reintentos exponenciales en caso de error
   * @param {Function} fetchFn - Función a ejecutar
   * @param {number} maxRetries - Número máximo de reintentos
   * @param {number} baseDelay - Delay base en ms
   */
  const fetchWithRetry = async (fetchFn, maxRetries = 3, baseDelay = 1000) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fetchFn();
      } catch (error) {
        if (attempt === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt)));
      }
    }
  };

  /**
   * Realiza peticiones API con cache y deduplicación
   * - Evita múltiples llamadas al mismo endpoint
   * - Cachea resultados por 5 minutos
   * - Deduplica peticiones pendientes
   * 
   * @param {string} url - URL del endpoint
   * @param {string} cacheKey - Clave para el cache
   * @returns {Promise<any>} Datos de la API
   */
  const optimizedFetchApiData = async (url, cacheKey) => {
    const fullKey = `${url}-${cacheKey}`;
    
    // Verificar cache (TTL: 5 minutos)
    const cached = requestCache.current.get(fullKey);
    if (cached && Date.now() - cached.timestamp < 300000) {
      return cached.data;
    }
    
    // Verificar si hay una petición pendiente
    if (pendingRequests.current.has(fullKey)) {
      return pendingRequests.current.get(fullKey);
    }
    
    // Crear nueva petición con retry
    const requestPromise = fetchWithRetry(async () => {
      return new Promise((resolve, reject) => {
        Crud_Catalogos(url, "", "get", "", {})
          .then((data) => {
            requestCache.current.set(fullKey, { data, timestamp: Date.now() });
            setApiCache(prev => ({ ...prev, [cacheKey]: data }));
            apiCacheRef.current = { ...apiCacheRef.current, [cacheKey]: data };
            resolve(data);
          })
          .catch(reject);
      });
    });
    
    pendingRequests.current.set(fullKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      pendingRequests.current.delete(fullKey);
    }
  };

  /**
   * Procesa items en lotes para evitar bloquear la UI
   * 
   * @param {Array} items - Items a procesar
   * @param {number} batchSize - Tamaño del lote
   * @param {Function} processor - Función procesadora (item, index, allItems) => result
   * @param {Function} onProgress - Callback de progreso ({ current, total })
   * @returns {Promise<Array>} Resultados procesados
   */
  /**
   * Procesa items en lotes para evitar bloquear la UI
   * 
   * @param {Array} items - Items a procesar
   * @param {number} batchSize - Tamaño del lote
   * @param {Function} processor - Función procesadora (item, index, allItems) => result
   * @param {Function} onProgress - Callback de progreso ({ current, total })
   * @returns {Promise<Array>} Resultados procesados
   */
  const processBatch = async (items, batchSize, processor, onProgress) => {
    const results = [];
    const total = items.length;
    
    for (let i = 0; i < total; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      
      for (let j = 0; j < batch.length; j++) {
        const result = await processor(batch[j], i + j, items);
        if (result !== null) { // Solo agregar si no es null (fila vacía)
          results.push(result);
        }
        
        if (onProgress) {
          onProgress({ current: i + j + 1, total });
        }
      }
      
      // Pausa para permitir que la UI se actualice
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    
    return results;
  };

  // ==================== GENERACIÓN DE PLANTILLA EXCEL ====================
  
  /**
   * Genera y descarga plantilla Excel con validaciones
   * 
   * PROCESO:
   * 1. Crea workbook con hoja "Plantilla"
   * 2. Agrega headers desde config
   * 3. Carga datos de APIs necesarias
   * 4. Crea hoja oculta "Validaciones" con opciones para selectores
   * 5. Aplica validaciones de lista a columnas específicas
   * 6. Descarga archivo
   */
  /**
   * Genera y descarga plantilla Excel con validaciones
   * 
   * PROCESO:
   * 1. Crea workbook con hoja "Plantilla"
   * 2. Agrega headers desde config
   * 3. Carga datos de APIs necesarias
   * 4. Crea hoja oculta "Validaciones" con opciones para selectores
   * 5. Aplica validaciones de lista a columnas específicas
   * 6. Descarga archivo
   */
  const downloadTemplate = async () => {
    setIsGeneratingTemplate(true);
    setShowLoadingModal(true);
    setTemplateProgress(0);
    
    // Simular progreso
    const progressInterval = setInterval(() => {
      setTemplateProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 200);
    
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Plantilla');
      
      // Agregar headers
      worksheet.addRow(config.headers);
      
      // Cargar datos de APIs y esperar a que terminen
      const localCache = {};
      if (config.apiLoaders) {
        const promises = config.apiLoaders.map(async (loader) => {
          try {
            const data = await optimizedFetchApiData(loader.url, loader.cacheKey);
            localCache[loader.cacheKey] = data;
            return data;
          } catch (error) {
            console.warn(`Error cargando ${loader.cacheKey}:`, error);
            return null;
          }
        });
        
        // Esperar a que todas las APIs terminen
        await Promise.all(promises);
        
        // Procesar datos si hay un procesador configurado
        if (config.dataProcessor && typeof config.dataProcessor === 'function') {
          config.dataProcessor(localCache);
        }
      }
      
      // Crear hoja de validaciones oculta
      const validationSheet = workbook.addWorksheet('Validaciones');
      
      // Crear hoja de datos para fórmulas (solo para operadores)
      let datosSheet = null;
      if (config.title?.includes('Operadores')) {
        datosSheet = workbook.addWorksheet('Datos');
        
        // Agregar datos de tractos
        const tractos = localCache.tractos || [];
        datosSheet.addRow(['TRACTOS', '', '', '', '']);
        datosSheet.addRow(['Placa', 'Economico', 'Marca', 'Color', 'Modelo']);
        tractos.forEach(tracto => {
          datosSheet.addRow([
            tracto.placa,
            tracto.economico || '',
            tracto.idUnidadMarca?.nombre || '',
            tracto.color || '',
            tracto.modelo || ''
          ]);
        });
        
        // Agregar datos de remolques
        const remolques = localCache.remolques || [];
        const startRemolqueRow = tractos.length + 5;
        datosSheet.getCell(`A${startRemolqueRow}`).value = 'REMOLQUES';
        datosSheet.addRow(['Placa', 'Economico', 'Tipo']);
        remolques.forEach(remolque => {
          datosSheet.addRow([
            remolque.placa,
            remolque.economico || '',
            remolque.remolquesTipos?.nombre || ''
          ]);
        });

        datosSheet.state = 'hidden';
      }
      
      if (config.validations) {
        let colIndex = 1;
        
        for (const validation of config.validations) {
          const colLetter = String.fromCharCode(64 + colIndex);
          validationSheet.getCell(`${colLetter}1`).value = validation.name;
          
          // Obtener opciones (estáticas o desde API)
          let options = [];
          if (validation.type === 'static') {
            options = validation.options || [];
          } else if (validation.type === 'api') {
            const data = localCache[validation.cacheKey];
            if (data && validation.mapper) {
              try {
                options = validation.mapper(data);
              } catch (error) {
                options = [];
              }
            }
          }
          
          // Agregar opciones a la hoja de validaciones
          if (options.length > 0) {
            options.forEach((opt, idx) => {
              validationSheet.getCell(`${colLetter}${idx + 2}`).value = opt;
            });
            
            // Aplicar validación de lista a la columna correspondiente
            const colNum = config.headers.indexOf(validation.column) + 1;
            if (colNum > 0) {
              const colLetterData = String.fromCharCode(64 + colNum);
              
              // --- CAMBIO AQUÍ: Validación condicional para remolque2Placa ---
              if (validation.column === 'remolque2Placa') {
                const esFullColNum = config.headers.indexOf('esFull') + 1;
                const esFullLetter = String.fromCharCode(64 + esFullColNum);
                
                // Aplicamos la fórmula: Si esFull = "Activo", usa el rango; si no, devuelve un rango vacío ("")
                worksheet.dataValidations.add(`${colLetterData}2:${colLetterData}1000`, {
                  type: 'list',
                  allowBlank: true,
                  formulae: [`=IF(${esFullLetter}2="Activo", Validaciones!$${colLetter}$2:$${colLetter}$${options.length + 1}, " ")`],
                  showErrorMessage: true,
                  errorTitle: 'Acción no permitida',
                  error: 'Solo puedes elegir un segundo remolque si el viaje es FULL (Activo).'
                });
              } else {
                // Validación normal para el resto de las columnas
                worksheet.dataValidations.add(`${colLetterData}2:${colLetterData}1000`, {
                  type: 'list',
                  allowBlank: true,
                  formulae: [`Validaciones!$${colLetter}$2:$${colLetter}$${options.length + 1}`]
                });
              }
              // --- FIN DEL CAMBIO ---
            }
          }
          colIndex++;
        }
      }
      
      // Agregar fórmulas automáticas para operadores
      if (config.title?.includes('Operadores') && datosSheet) {
        const tractos = localCache.tractos || [];
        const remolques = localCache.remolques || [];
        
        // Fórmulas para datos de tracto
        const tractorPlacaCol = config.headers.indexOf('tractorPlaca') + 1;
        const tractorEconomicoCol = config.headers.indexOf('tractorEconomico') + 1;
        const tractorMarcaCol = config.headers.indexOf('tractorMarca') + 1;
        const tractorColorCol = config.headers.indexOf('tractorColor') + 1;
        const tractorModeloCol = config.headers.indexOf('tractorModelo') + 1;
        
        // Fórmulas para datos de remolque
        const remolquePlacaCol = config.headers.indexOf('remolquePlaca') + 1;
        const remolqueEconomicoCol = config.headers.indexOf('remolqueEconomico') + 1;
        const remolqueTipoCol = config.headers.indexOf('remolqueTipo') + 1;
        
        // Fórmulas para datos de remolque 2
        const remolque2PlacaCol = config.headers.indexOf('remolque2Placa') + 1;
        const remolque2EconomicoCol = config.headers.indexOf('remolque2Economico') + 1;
        const remolque2TipoCol = config.headers.indexOf('remolque2Tipo') + 1;
        const esFullCol = config.headers.indexOf('esFull') + 1;
        
        for (let row = 2; row <= 1000; row++) {
          // Fórmulas para tracto
          if (tractorEconomicoCol > 0) {
            worksheet.getCell(row, tractorEconomicoCol).value = {
              formula: `IF(${String.fromCharCode(64 + tractorPlacaCol)}${row}="","",IFERROR(VLOOKUP(${String.fromCharCode(64 + tractorPlacaCol)}${row},Datos!A3:E${tractos.length + 2},2,FALSE),""))`
            };
          }
          if (tractorMarcaCol > 0) {
            worksheet.getCell(row, tractorMarcaCol).value = {
              formula: `IF(${String.fromCharCode(64 + tractorPlacaCol)}${row}="","",IFERROR(VLOOKUP(${String.fromCharCode(64 + tractorPlacaCol)}${row},Datos!A3:E${tractos.length + 2},3,FALSE),""))`
            };
          }
          if (tractorColorCol > 0) {
            worksheet.getCell(row, tractorColorCol).value = {
              formula: `IF(${String.fromCharCode(64 + tractorPlacaCol)}${row}="","",IFERROR(VLOOKUP(${String.fromCharCode(64 + tractorPlacaCol)}${row},Datos!A3:E${tractos.length + 2},4,FALSE),""))`
            };
          }
          if (tractorModeloCol > 0) {
            worksheet.getCell(row, tractorModeloCol).value = {
              formula: `IF(${String.fromCharCode(64 + tractorPlacaCol)}${row}="","",IFERROR(VLOOKUP(${String.fromCharCode(64 + tractorPlacaCol)}${row},Datos!A3:E${tractos.length + 2},5,FALSE),""))`
            };
          }
          
          // Fórmulas para remolque 1
          const remolqueStartRow = tractos.length + 7;
          if (remolqueEconomicoCol > 0) {
            worksheet.getCell(row, remolqueEconomicoCol).value = {
              formula: `IF(${String.fromCharCode(64 + remolquePlacaCol)}${row}="","",IFERROR(VLOOKUP(${String.fromCharCode(64 + remolquePlacaCol)}${row},Datos!A${remolqueStartRow}:C${remolqueStartRow + remolques.length - 1},2,FALSE),""))`
            };
          }
          if (remolqueTipoCol > 0) {
            worksheet.getCell(row, remolqueTipoCol).value = {
              formula: `IF(${String.fromCharCode(64 + remolquePlacaCol)}${row}="","",IFERROR(VLOOKUP(${String.fromCharCode(64 + remolquePlacaCol)}${row},Datos!A${remolqueStartRow}:C${remolqueStartRow + remolques.length - 1},3,FALSE),""))`
            };
          }
          
          // Fórmulas para remolque 2 (solo si esFull = "Activo")
          if (remolque2EconomicoCol > 0) {
            worksheet.getCell(row, remolque2EconomicoCol).value = {
              formula: `IF(OR(${String.fromCharCode(64 + esFullCol)}${row}<>"Activo",${String.fromCharCode(64 + remolque2PlacaCol)}${row}=""),"",IFERROR(VLOOKUP(${String.fromCharCode(64 + remolque2PlacaCol)}${row},Datos!A${remolqueStartRow}:C${remolqueStartRow + remolques.length - 1},2,FALSE),""))`
            };
          }
          if (remolque2TipoCol > 0) {
            worksheet.getCell(row, remolque2TipoCol).value = {
              formula: `IF(OR(${String.fromCharCode(64 + esFullCol)}${row}<>"Activo",${String.fromCharCode(64 + remolque2PlacaCol)}${row}=""),"",IFERROR(VLOOKUP(${String.fromCharCode(64 + remolque2PlacaCol)}${row},Datos!A${remolqueStartRow}:C${remolqueStartRow + remolques.length - 1},3,FALSE),""))`
            };
          }
        }
        
        // --- NUEVO: OCULTAR TEXTO SI NO ES FULL ---
        if (config.title?.includes('Operadores')) {
          const remolque2ColNum = config.headers.indexOf('remolque2Placa') + 1;
          const esFullColNum = config.headers.indexOf('esFull') + 1;

          if (remolque2ColNum > 0 && esFullColNum > 0) {
            const rem2Letter = String.fromCharCode(64 + remolque2ColNum);
            const esFullLetter = String.fromCharCode(64 + esFullColNum);
          
            for (let row = 2; row <= 1000; row++) {
              worksheet.addConditionalFormatting({
                ref: `${rem2Letter}${row}`,
                rules: [
                  {
                    type: 'expression',
                    // Si la celda esFull NO es "Activo", ponemos el texto en blanco
                    formulae: [`=AND(${esFullLetter}${row}<>"Activo", ${esFullLetter}${row}<>"")`],
                    style: { 
                      // Ponemos la fuente blanca (ARGB: Blanco es FFFFFFFF)
                      // Al no poner 'fill', el fondo sigue siendo transparente/blanco normal
                      // y las rayas de la cuadrícula se siguen viendo.
                      font: { color: { argb: 'FFFFFFFF' } }
                    },
                  }
                ]
              });
            }
          }
        }
        
        // Colorear headers de campos automáticos para indicar que se llenan solos
        const autoColumns = [
          'tractorEconomico', 'tractorMarca', 'tractorColor', 'tractorModelo',
          'remolqueEconomico', 'remolqueTipo', 'remolque2Economico', 'remolque2Tipo'
        ];
        
        autoColumns.forEach(colName => {
          const colNum = config.headers.indexOf(colName) + 1;
          if (colNum > 0) {
            worksheet.getCell(1, colNum).fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFE6F3FF' }
            };
            worksheet.getCell(1, colNum).note = 'Se llena automáticamente - No editable';
            
            // Proteger las celdas con fórmulas
            for (let row = 2; row <= 1000; row++) {
              worksheet.getCell(row, colNum).protection = { locked: true };
            }
          }
        });
        
        // Desproteger las celdas editables (incluyendo remolque2Placa)
        const editableColumns = [
          'nombre', 'apellidoPaterno', 'apellidoMaterno', 'contacto', 
          'vigenciaLicencia', 'tipoLicencia', 'tractorPlaca', 'remolquePlaca',
          'esFull', 'remolque2Placa'
        ];
        
        editableColumns.forEach(colName => {
          const colNum = config.headers.indexOf(colName) + 1;
          if (colNum > 0) {
            for (let row = 2; row <= 1000; row++) {
              worksheet.getCell(row, colNum).protection = { locked: false };
            }
          }
        });
        
        // Activar protección de la hoja
        worksheet.protect('', {
          selectLockedCells: true,
          selectUnlockedCells: true,
          formatCells: false,
          formatColumns: true,
          formatRows: true,
          insertColumns: false,
          insertRows: false,
          insertHyperlinks: false,
          deleteColumns: false,
          deleteRows: false,
          sort: false,
          autoFilter: false,
          pivotTables: false
        });
        
        // Redimensionar automáticamente todas las columnas
        worksheet.columns.forEach((column, index) => {
          let maxLength = 0;
          const columnLetter = String.fromCharCode(65 + index);
          
          // Revisar el header
          const headerCell = worksheet.getCell(`${columnLetter}1`);
          if (headerCell.value) {
            maxLength = Math.max(maxLength, headerCell.value.toString().length);
          }
          
          // Revisar algunas filas de datos para calcular el ancho
          for (let row = 2; row <= Math.min(10, worksheet.rowCount); row++) {
            const cell = worksheet.getCell(`${columnLetter}${row}`);
            if (cell.value) {
              const cellLength = cell.value.toString().length;
              maxLength = Math.max(maxLength, cellLength);
            }
          }
          
          // Establecer ancho mínimo de 10 y máximo de 50
          column.width = Math.max(10, Math.min(50, maxLength + 2));
        });
      }
      
      // Ocultar hoja de validaciones
      validationSheet.state = 'hidden';
      
      // Crear hoja de instrucciones
      const instructionsSheet = workbook.addWorksheet('Instrucciones');
      
      if (config.title?.includes('Operadores')) {
        instructionsSheet.addRow(['INSTRUCCIONES PARA CARGA MASIVA DE OPERADORES']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['1. CAMPOS OBLIGATORIOS:']);
        instructionsSheet.addRow(['   • nombre: Nombre(s) del operador']);
        instructionsSheet.addRow(['   • contacto: Teléfono o medio de comunicación']);
        instructionsSheet.addRow(['   • vigenciaLicencia: Fecha en formato YYYY-MM-DD (ejemplo: 2026-10-20)']);
        instructionsSheet.addRow(['   • tipoLicencia: Tipo de licencia (Federal, Estatal, etc.)']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['2. CAMPOS OPCIONALES:']);
        instructionsSheet.addRow(['   • apellidoPaterno: Apellido paterno del operador']);
        instructionsSheet.addRow(['   • apellidoMaterno: Apellido materno del operador']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['3. FORMATO DE FECHA:']);
        instructionsSheet.addRow(['   • La fecha de vigencia DEBE estar en formato YYYY-MM-DD']);
        instructionsSheet.addRow(['   • Ejemplo correcto: 2026-10-20']);
        instructionsSheet.addRow(['   • Ejemplo incorrecto: 20/10/2026, 20-oct-2026']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['4. NOTAS IMPORTANTES:']);
        instructionsSheet.addRow(['   • Solo se procesarán filas con los campos obligatorios completos']);
        instructionsSheet.addRow(['   • No se permiten operadores duplicados (mismo nombre y apellidos)']);
        instructionsSheet.addRow(['   • El operador se asociará automáticamente al transportista']);
      } else if (config.title?.includes('Remolques')) {
        instructionsSheet.addRow(['INSTRUCCIONES PARA CARGA MASIVA DE REMOLQUES']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['1. CAMPOS OBLIGATORIOS:']);
        instructionsSheet.addRow(['   • placa: Placa del remolque (ejemplo: REM123)']);
        instructionsSheet.addRow(['   • economico: Número económico del remolque']);
        instructionsSheet.addRow(['   • tipo: Seleccionar de la lista desplegable']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['2. INSTRUCCIONES:']);
        instructionsSheet.addRow(['   • Usar las listas desplegables para el tipo']);
        instructionsSheet.addRow(['   • No dejar campos obligatorios vacíos']);
        instructionsSheet.addRow(['   • Las placas no pueden repetirse']);
        instructionsSheet.addRow(['   • Guardar el archivo antes de subirlo']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['3. NOTAS IMPORTANTES:']);
        instructionsSheet.addRow(['   • Solo se procesarán filas con todos los campos completos']);
        instructionsSheet.addRow(['   • El remolque se asociará automáticamente al transportista']);
      } else {
        instructionsSheet.addRow(['INSTRUCCIONES PARA CARGA MASIVA DE TRACTOS']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['1. CAMPOS OBLIGATORIOS:']);
        instructionsSheet.addRow(['   • Placa: Placa del tracto (ejemplo: ABC123)']);
        instructionsSheet.addRow(['   • Económico: Número económico del tracto']);
        instructionsSheet.addRow(['   • Marca: Seleccionar de la lista desplegable']);
        instructionsSheet.addRow(['   • Color: Seleccionar de la lista desplegable']);
        instructionsSheet.addRow(['   • Modelo: Seleccionar año de la lista desplegable']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['2. INSTRUCCIONES:']);
        instructionsSheet.addRow(['   • Usar las listas desplegables en cada columna']);
        instructionsSheet.addRow(['   • No dejar campos obligatorios vacíos']);
        instructionsSheet.addRow(['   • Las placas no pueden repetirse']);
        instructionsSheet.addRow(['   • Guardar el archivo antes de subirlo']);
        instructionsSheet.addRow([]);
        instructionsSheet.addRow(['3. NOTAS IMPORTANTES:']);
        instructionsSheet.addRow(['   • Solo se procesarán filas con todos los campos completos']);
      }
      
      // Formatear hoja de instrucciones
      instructionsSheet.getCell('A1').font = { bold: true, size: 14 };
      instructionsSheet.getCell('A3').font = { bold: true };
      instructionsSheet.getCell('A10').font = { bold: true };
      instructionsSheet.getCell('A16').font = { bold: true };
      instructionsSheet.getColumn('A').width = 60;
      
      // Generar y descargar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = config.templateName || 'plantilla.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      // Completar progreso
      setTemplateProgress(100);
      
    } catch (error) {
      console.error('Error generando plantilla:', error);
      alert('Error al generar la plantilla Excel: ' + error.message);
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setIsGeneratingTemplate(false);
        setShowLoadingModal(false);
        setTemplateProgress(0);
      }, 500);
    }
  };

  // ==================== VALIDACIÓN DE DATOS ====================
  
  /**
   * Valida una fila del Excel
   * 
   * @param {Object} row - Datos de la fila
   * @param {number} rowIndex - Índice de la fila
   * @param {Array} allData - Todos los datos del archivo
   * @returns {Object} { rowIndex, data, isValid, errors }
   */
  /**
   * Valida una fila del Excel
   * 
   * @param {Object} row - Datos de la fila
   * @param {number} rowIndex - Índice de la fila
   * @param {Array} allData - Todos los datos del archivo
   * @returns {Object} { rowIndex, data, isValid, errors }
   */
  const validateRow = (row, rowIndex, allData) => {
    const errors = [];
    
    // Validar campos requeridos
    config.requiredFields?.forEach(field => {
      if (!row[field] || row[field].toString().trim() === '') {
        errors.push(`El campo "${field}" es obligatorio`);
      }
    });
    
    // Ejecutar validaciones personalizadas
    if (config.customValidations) {
      config.customValidations.forEach(validation => {
        const error = validation.validator(row, rowIndex, allData, apiCache);
        if (error) errors.push(error);
      });
    }
    
    return {
      rowIndex: rowIndex + 1,
      data: row,
      isValid: errors.length === 0,
      errors
    };
  };

  // ==================== PROCESAMIENTO DE ARCHIVO ====================
  
  /**
   * Procesa archivo Excel y valida datos
   * 
   * PROCESO:
   * 1. Lee archivo Excel
   * 2. Extrae headers y datos
   * 3. Valida cada fila en lotes
   * 4. Actualiza estado con resultados
   * 
   * @param {File} file - Archivo Excel
   */
  /**
   * Procesa archivo Excel y valida datos
   * 
   * PROCESO:
   * 1. Lee archivo Excel
   * 2. Extrae headers y datos
   * 3. Valida cada fila en lotes
   * 4. Actualiza estado con resultados
   * 
   * @param {File} file - Archivo Excel
   */
  const processExcel = async (file) => {
    setIsLoading(true);
    setIsValidating(false);
    setCsvData([]);
    setValidationResults([]);
    setFileName(file.name);

    try {
      // Cargar APIs necesarias antes de validar
      const localApiCache = {};
      if (config.apiLoaders) {
        const promises = config.apiLoaders.map(async (loader) => {
          try {
            const data = await optimizedFetchApiData(loader.url, loader.cacheKey);
            localApiCache[loader.cacheKey] = data;
            return data;
          } catch (error) {
            console.warn(`Error cargando ${loader.cacheKey}:`, error);
            return null;
          }
        });
        
        await Promise.all(promises);
        
        // Procesar datos si hay un procesador configurado
        if (config.dataProcessor && typeof config.dataProcessor === 'function') {
          config.dataProcessor(localApiCache);
        }
        
        // Sincronizar cache local con el state para que buildDTO lo use
        setApiCache(prev => ({ ...prev, ...localApiCache }));
        apiCacheRef.current = { ...apiCacheRef.current, ...localApiCache };
      }

      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      
      const worksheet = workbook.getWorksheet('Plantilla') || workbook.getWorksheet(1);
      if (!worksheet) {
        throw new Error('No se encontró una hoja de trabajo válida');
      }

      const rows = [];
      const headers = [];
      
      // Extraer headers (primera fila)
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell, colNumber) => {
        headers[colNumber - 1] = cell.text || cell.value || '';
      });
      
      // Extraer datos (desde fila 2)
      for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
        const row = worksheet.getRow(rowNumber);
        const rowData = {};
        let hasData = false;
        
        headers.forEach((header, index) => {
          const cell = row.getCell(index + 1);
          let value = cell.text || cell.value || '';
          
          // Manejo especial para fechas
          if (header === 'vigenciaLicencia') {
            if (cell.value instanceof Date) {
              // Si es una fecha de Excel, formatearla como YYYY-MM-DD
              const fecha = cell.value;
              value = fecha.getFullYear() + '-' + 
                     String(fecha.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(fecha.getDate()).padStart(2, '0');
            } else if (typeof cell.value === 'number' && cell.value > 25569) {
              // Si es un número serial de Excel (fechas después de 1900), convertirlo
              const fecha = new Date((cell.value - 25569) * 86400 * 1000);
              value = fecha.getFullYear() + '-' + 
                     String(fecha.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(fecha.getDate()).padStart(2, '0');
            } else if (typeof value === 'string' && value.includes('/')) {
              // Si viene en formato DD/MM/YYYY o MM/DD/YYYY, convertir a YYYY-MM-DD
              const partes = value.split('/');
              if (partes.length === 3) {
                // Asumir DD/MM/YYYY
                const dia = partes[0].padStart(2, '0');
                const mes = partes[1].padStart(2, '0');
                const año = partes[2];
                value = `${año}-${mes}-${dia}`;
              }
            }
          }
          
          rowData[header] = value.toString().trim();
          if (value.toString().trim() !== '') hasData = true;
        });
        
        if (hasData) {
          rows.push(rowData);
        }
      }
      
      if (rows.length === 0) {
        throw new Error('El archivo no contiene datos');
      }

      setCsvData(rows);

      // Validar datos en lotes usando el cache local
      setIsValidating(true);
      setValidationProgress({ current: 0, total: rows.length });
      
      const results = await processBatch(
        rows,
        50, // 50 filas por lote
        async (row, index, allData) => validateRowWithCache(row, index, allData, localApiCache),
        (progress) => setValidationProgress(progress)
      );
      
      setIsValidating(false);
      setValidationResults(results);
    } catch (error) {
      console.error('Error procesando Excel:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
      setIsValidating(false);
    }
  };

  // Nueva función de validación que usa cache local y completa datos automáticos
  const validateRowWithCache = (row, rowIndex, allData, localCache) => {
    const errors = [];
    
    if (config.title?.includes('Tractos')) {
      // Validación específica para tractos - no necesita completar datos automáticos
      // Los datos vienen directamente del Excel
    } else {
      // Completar datos automáticos de tracto para operadores
      if (row.tractorPlaca && row.tractorPlaca.trim() !== '') {
        const tractos = localCache.tractos || [];
        const tracto = tractos.find(t => 
          t.placa.toLowerCase().trim() === row.tractorPlaca.toLowerCase().trim()
        );
        if (tracto) {
          row.tractorEconomico = tracto.economico || '';
          row.tractorMarca = tracto.idUnidadMarca?.nombre || '';
          row.tractorColor = tracto.color || '';
          row.tractorModelo = tracto.modelo || '';
        }
      }
      
      // Completar datos automáticos de remolque 1
      if (row.remolquePlaca && row.remolquePlaca.trim() !== '') {
        const remolques = localCache.remolques || [];
        const remolque = remolques.find(r => 
          r.placa.toLowerCase().trim() === row.remolquePlaca.toLowerCase().trim()
        );
        if (remolque) {
          row.remolqueEconomico = remolque.economico || '';
          row.remolqueTipo = remolque.remolquesTipos?.nombre || '';
        }
      }
      
      // Completar datos automáticos de remolque 2 (si esFull = Activo)
      if (row.esFull?.toLowerCase() === 'activo' && row.remolque2Placa && row.remolque2Placa.trim() !== '') {
        const remolques = localCache.remolques || [];
        const remolque2 = remolques.find(r => 
          r.placa.toLowerCase().trim() === row.remolque2Placa.toLowerCase().trim()
        );
        if (remolque2) {
          row.remolque2Economico = remolque2.economico || '';
          row.remolque2Tipo = remolque2.remolquesTipos?.nombre || '';
        }
      } else if (row.esFull?.toLowerCase() !== 'activo') {
        // Limpiar campos de remolque2 si no es Activo
        row.remolque2Placa = '';
        row.remolque2Economico = '';
        row.remolque2Tipo = '';
      }
    }
    
    // Verificar si la fila está completamente vacía (solo campos principales)
    const mainFields = config.requiredFields || Object.keys(row);
    const hasMainData = mainFields.some(field => 
      row[field] && row[field].toString().trim() !== ''
    );
    
    if (!hasMainData) {
      return null; // Omitir filas vacías
    }
    
    // Validar campos requeridos
    config.requiredFields?.forEach(field => {
      if (!row[field] || row[field].toString().trim() === '') {
        errors.push(`El campo "${field}" es obligatorio`);
      }
    });
    
    // Ejecutar validaciones personalizadas con cache local
    if (config.customValidations) {
      config.customValidations.forEach(validation => {
        const error = validation.validator(row, rowIndex, allData, localCache);
        if (error) errors.push(error);
      });
    }
    
    return {
      rowIndex: rowIndex + 1,
      data: row,
      isValid: errors.length === 0,
      errors
    };
  };

  /**
   * Maneja selección de archivo
   */
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.xlsx')) {
      setFileName(file.name);
      processExcel(file);
    } else {
      alert('Selecciona un archivo Excel (.xlsx) válido');
    }
    event.target.value = '';
  };

  // ==================== CARGA DE DATOS A API ====================
  
  /**
   * Carga datos válidos a la API
   * 
   * PROCESO:
   * 1. Filtra solo registros válidos
   * 2. Por cada registro:
   *    - Construye DTO usando config.buildDTO
   *    - Envía a API
   *    - Ejecuta post-procesamiento si existe
   *    - Registra resultado (éxito/error)
   * 3. Muestra progreso en modal
   */
  /**
   * Carga datos válidos a la API
   * 
   * PROCESO:
   * 1. Filtra solo registros válidos
   * 2. Por cada registro:
   *    - Construye DTO usando config.buildDTO
   *    - Envía a API
   *    - Ejecuta post-procesamiento si existe
   *    - Registra resultado (éxito/error)
   * 3. Muestra progreso en modal
   */
  const loadDataViaAPI = async () => {
    const validData = validationResults.filter(r => r.isValid).map(r => r.data);
    
    if (validData.length === 0) {
      alert('No hay datos válidos para cargar');
      return;
    }

    setIsLoadingData(true);
    setShowLoadingModal(true);
    setLoadingProgress({ current: 0, total: validData.length });
    setLoadingResults([]);

    for (let i = 0; i < validData.length; i++) {
      const row = validData[i];
      
      setLoadingProgress({ current: i, total: validData.length });
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        let result;
        
        if (config.customSave) {
          // Flujo personalizado completo (ej: tractos que manejan placa existente)
          result = await config.customSave(row, apiCacheRef.current, config.idTransportista);
        } else {
          // Flujo estándar: buildDTO + API + postProcess
          const dto = config.buildDTO(row, apiCacheRef.current);
          
          result = await Crud_Catalogos(
            config.apiEndpoint, 
            "", 
            config.apiMethod, 
            "", 
            dto,
            "",
            "",
            "",
            []
          ).catch(error => {
            console.error('❌ ERROR DETALLADO:', {
              status: error.response?.status,
              data: error.response?.data,
              message: error.message,
              dto: dto
            });
            throw error;
          });
          
          if (config.postProcess && result) {
            await config.postProcess(result, config.idTransportista, row);
          }
        }
        
        setLoadingResults(prev => [...prev, {
          row: i + 1,
          data: row,
          success: true,
          response: result
        }]);
      } catch (error) {
        setLoadingResults(prev => [...prev, {
          row: i + 1,
          data: row,
          success: false,
          error: error.message || error.toString()
        }]);
      }
      
      setLoadingProgress({ current: i + 1, total: validData.length });
    }

    setIsLoadingData(false);
    
    // Notificar que se cargaron datos exitosamente
    if (config.onDataLoaded) {
      config.onDataLoaded();
    }
  };

  // ==================== CÁLCULOS DERIVADOS ====================
  
  const validCount = validationResults.filter(r => r.isValid).length;
  const invalidCount = validationResults.filter(r => !r.isValid).length;

  // ==================== RENDER ====================
  
  return (
    <div className="csv-importer-container">
      {!config.hideHeader && (
        <div className="csv-importer-header">
          <h1 className="csv-importer-title">{config.title}</h1>
          <p className="csv-importer-subtitle">{config.subtitle}</p>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <button 
              onClick={downloadTemplate} 
              className="csv-importer-template-button"
              disabled={isGeneratingTemplate}
            >
              {isGeneratingTemplate ? (
                <>
                  <div className="spinner" style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid #f3f3f3', 
                    borderTop: '2px solid #3498db', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  Generando...
                </>
              ) : (
                <>
                  <FileText />
                  Descargar Plantilla
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {config.hideHeader && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <button 
            onClick={downloadTemplate} 
            className="csv-importer-template-button"
            disabled={isGeneratingTemplate}
          >
            {isGeneratingTemplate ? (
              <>
                <div className="spinner" style={{ 
                  width: '16px', 
                  height: '16px', 
                  border: '2px solid #f3f3f3', 
                  borderTop: '2px solid #3498db', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite' 
                }}></div>
                Generando...
              </>
            ) : (
              <>
                <FileText />
                Descargar Plantilla
              </>
            )}
          </button>
        </div>
      )}

      <div className="csv-importer-layout">
        <div className="csv-importer-column csv-upload-area">
          <h3 className="csv-upload-title">Cargar Archivo CSV o Excel</h3>
          <div className="csv-upload-dropzone">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xlsx"
              className="hidden"
            />
            
            <div className="csv-upload-content">
              {!fileName ? (
                <div>
                  <Upload className="csv-upload-icon" />
                  <p className="csv-upload-main-text">Selecciona tu archivo CSV o Excel</p>
                  <p className="csv-upload-sub-text">Descarga la plantilla Excel para ver la estructura correcta</p>
                  <button onClick={() => fileInputRef.current?.click()} className="csv-upload-button">
                    Elegir Archivo
                  </button>
                </div>
              ) : (
                <div>
                  <FileText className="csv-upload-selected-icon" />
                  <p className="csv-upload-selected-text">Archivo cargado:</p>
                  <p className="csv-upload-filename">{fileName}</p>
                  <button onClick={() => fileInputRef.current?.click()} className="csv-upload-change-button">
                    Cambiar Archivo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="csv-importer-column">
          {validationResults.length > 0 && !isLoading && !isValidating ? (
            <div className="csv-summary-container">
              <h2 className="csv-summary-title">Resumen de Validación</h2>
              <div className="csv-summary-grid">
                <div className="csv-summary-card">
                  <div className="csv-summary-number">{validationResults.length}</div>
                  <div className="csv-summary-label">Total de registros</div>
                </div>
                <div className="csv-summary-card-green">
                  <div className="csv-summary-number-green">{validCount}</div>
                  <div className="csv-summary-label-green">Registros válidos</div>
                </div>
                <div className="csv-summary-card-red">
                  <div className="csv-summary-number-red">{invalidCount}</div>
                  <div className="csv-summary-label-red">Registros con errores</div>
                </div>
              </div>

              <div className="csv-summary-actions">
                <button onClick={() => setIsModalOpen(true)} className="csv-summary-button">
                  Ver Detalles de Validación
                </button>
                
                {validCount > 0 && (
                  <button onClick={loadDataViaAPI} className="csv-summary-button-green" disabled={isLoadingData}>
                    {isLoadingData ? 'Cargando...' : `Cargar Datos (${validCount})`}
                  </button>
                )}
              </div>

              <div className="csv-info-recommendation" style={{ marginTop: '1.5rem' }}>
                <div className="csv-info-recommendation-title">
                  <AlertCircle className="csv-info-recommendation-icon" />
                  <span className="csv-info-recommendation-text">Observaciones Importantes</span>
                </div>
                <div className="csv-info-recommendation-description">
                  <p>✅ <strong>Plantilla con validaciones:</strong></p>
                  <p>• Las columnas tienen selectores desplegables</p>
                  <p>• Cada fila tiene opciones específicas de marca y color</p>
                  <p>• Revisa la hoja "Instrucciones" para más detalles</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="csv-info-area">
              <h3 className="csv-info-title">Estado del Análisis</h3>
              <p className="csv-upload-sub-text">
                {isLoading ? "Procesando archivo..." : isValidating ? "Validando datos..." : "Selecciona un archivo CSV para comenzar el análisis"}
              </p>

              <div className="csv-info-recommendation">
                <div className="csv-info-recommendation-title">
                  <AlertCircle className="csv-info-recommendation-icon" />
                  <span className="csv-info-recommendation-text">Consejos para usar la plantilla Excel</span>
                </div>
                <div className="csv-info-recommendation-description">
                  <p>• Descarga la plantilla con validaciones incluidas</p>
                  <p>• Usa los selectores desplegables en las columnas</p>
                  <p>• Revisa la hoja "Instrucciones" para más información</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(isLoading || isValidating) && (
        <div className="csv-fullscreen-loader">
          <div className="csv-fullscreen-overlay"></div>
          <div className="csv-fullscreen-content">
            <div className="csv-loading-truck-container">
              <div className="csv-loading-truck">🚛</div>
              <div className="csv-loading-documents">📋</div>
              <div className="csv-loading-road"></div>
            </div>
            
            <div className="csv-loading-text">
              {isValidating ? "Validando archivo" : "Procesando archivo"}
            </div>
            <div className="csv-loading-subtext">
              {isValidating 
                ? `Verificando datos... ${validationProgress.current}/${validationProgress.total}` 
                : "Leyendo archivo..."
              }
            </div>
            
            <div className="csv-loading-progress">
              <div className="csv-loading-progress-bar">
                <div 
                  className="csv-loading-progress-fill"
                  style={{
                    width: isValidating && validationProgress.total > 0 
                      ? `${(validationProgress.current / validationProgress.total) * 100}%`
                      : '50%'
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="csv-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="csv-modal">
            <div className="csv-modal-header">
              <h2 className="csv-modal-title">Resultados de Validación</h2>
              <button onClick={() => setIsModalOpen(false)} className="csv-modal-close">
                <X />
              </button>
            </div>

            <div className="csv-modal-content">
              <div className="csv-modal-summary">
                <div className="csv-modal-summary-grid">
                  <div className="csv-summary-card">
                    <div className="csv-summary-number">{validationResults.length}</div>
                    <div className="csv-summary-label">Total procesados</div>
                  </div>
                  <div className="csv-summary-card-green">
                    <div className="csv-summary-number-green">{validCount}</div>
                    <div className="csv-summary-label-green">Válidos</div>
                    <div className="csv-modal-summary-percentage csv-modal-summary-percentage-green">
                      {validationResults.length > 0 ? Math.round((validCount / validationResults.length) * 100) : 0}% del total
                    </div>
                  </div>
                  <div className="csv-summary-card-red">
                    <div className="csv-summary-number-red">{invalidCount}</div>
                    <div className="csv-summary-label-red">Con errores</div>
                    <div className="csv-modal-summary-percentage csv-modal-summary-percentage-red">
                      {validationResults.length > 0 ? Math.round((invalidCount / validationResults.length) * 100) : 0}% del total
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="csv-modal-results-title">Detalle por Registro</h3>
              <div className="csv-modal-results-container">
                {validationResults.map((result, index) => (
                  <div key={index} className={`csv-modal-result-item ${result.isValid ? 'csv-modal-result-valid' : 'csv-modal-result-invalid'}`}>
                    <div className="csv-modal-result-header">
                      <span className="csv-modal-result-name">
                        Registro {result.rowIndex}: {result.data.placa || result.data.nombre || 'Sin nombre'}
                      </span>
                      {result.isValid ? <CheckCircle /> : <AlertCircle />}
                    </div>
                    
                    {result.errors.length > 0 && (
                      <div className="csv-modal-result-errors">
                        <div className="csv-modal-result-errors-title">Errores encontrados:</div>
                        <div className="csv-modal-result-errors-list">
                          {result.errors.map((error, errorIndex) => (
                            <div key={errorIndex} className="csv-modal-result-error-item">
                              <X className="csv-modal-result-error-icon" />
                              <span>{error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="csv-modal-footer">
              <div className="csv-modal-footer-info">
                {invalidCount > 0 && (
                  <span className="csv-modal-footer-info-invalid">
                    {invalidCount} registros necesitan corrección
                  </span>
                )}
              </div>
              <button onClick={() => setIsModalOpen(false)} className="csv-modal-footer-button">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoadingModal && (
        <div className="csv-modal-overlay">
          <div className="csv-modal">
            <div className="csv-modal-header">
              <h2 className="csv-modal-title">
                {isGeneratingTemplate ? 'Generando Plantilla' : 'Cargando Datos'}
              </h2>
              {!isLoadingData && !isGeneratingTemplate && (
                <button onClick={() => setShowLoadingModal(false)} className="csv-modal-close">
                  <X />
                </button>
              )}
            </div>

            <div className="csv-modal-content">
              {isGeneratingTemplate ? (
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  {/* Camión animado */}
                  <div style={{ 
                    marginBottom: '20px',
                    animation: 'truck-move 2s ease-in-out infinite alternate'
                  }}>
                    <img 
                      src="/images/camion.png" 
                      alt="Camión" 
                      style={{ 
                        width: '120px', 
                        height: 'auto' 
                      }} 
                    />
                  </div>
                  
                  {/* Barra de progreso animada */}
                  <div style={{
                    width: '60%',
                    height: '8px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '20px',
                    margin: '0 auto 20px'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${templateProgress}%`,
                      background: 'linear-gradient(90deg, #3b82f6, #10b981)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '10px' }}>
                    {Math.round(templateProgress)}%
                  </div>
                  
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    Generando plantilla Excel...
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '10px' }}>
                    Por favor espere mientras se crea el archivo
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                    {Math.round((loadingProgress.current / loadingProgress.total) * 100)}%
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {loadingProgress.current}/{loadingProgress.total}
                  </div>
                </div>
              )}

              {!isGeneratingTemplate && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#ecfdf5', borderRadius: '8px' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>
                      {loadingResults.filter(r => r.success).length}
                    </div>
                    <div style={{ fontSize: '12px', color: '#059669' }}>Exitosos</div>
                  </div>
                  
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#fef2f2', borderRadius: '8px' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>
                      {loadingResults.filter(r => !r.success).length}
                    </div>
                    <div style={{ fontSize: '12px', color: '#dc2626' }}>Errores</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CargaMasivaGenerico;
